import { supabase } from '../lib/supabase';

const RoleService = {
  // Get all roles with musical information and song counts
  async getRoles(searchTerm = '') {
    try {
      // Use LEFT JOIN instead of INNER JOIN to include roles without musicals
      let query = supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          vocal_range,
          voice_type,
          role_type,
          is_principal,
          is_featured,
          age_range,
          created_at,
          updated_at,
          musical_id,
          assigned_talent_id,
          musicals (
            id,
            title,
            poster_url,
            composer,
            lyricist,
            year_premiered,
            genre,
            is_active
          )
        `)?.order('character_name', { ascending: true });

      // Apply search using individual filters with OR logic
      if (searchTerm?.trim()) {
        const searchLower = searchTerm?.trim()?.toLowerCase();
        
        // Use separate filters combined with OR logic - avoiding complex OR strings
        query = query?.or(`character_name.ilike.%${searchLower}%,description.ilike.%${searchLower}%,vocal_range.ilike.%${searchLower}%,voice_type.ilike.%${searchLower}%,role_type.ilike.%${searchLower}%`);
        
        // Search musical information separately and combine results
        const musicalSearchQuery = supabase?.from('roles')?.select(`
            id,
            character_name,
            description,
            vocal_range,
            voice_type,
            role_type,
            is_principal,
            is_featured,
            age_range,
            created_at,
            updated_at,
            musical_id,
            assigned_talent_id,
            musicals (
              id,
              title,
              poster_url,
              composer,
              lyricist,
              year_premiered,
              genre,
              is_active
            )
          `)?.not('musical_id', 'is', null)?.or(`musicals.title.ilike.%${searchLower}%,musicals.composer.ilike.%${searchLower}%,musicals.lyricist.ilike.%${searchLower}%`);

        // Execute both queries
        const [roleResults, musicalResults] = await Promise.all([
          query,
          musicalSearchQuery
        ]);

        if (roleResults?.error && musicalResults?.error) {
          console.error('Error fetching roles:', roleResults?.error);
          return { data: null, error: roleResults?.error };
        }

        // Combine and deduplicate results
        const allResults = [];
        const seenIds = new Set();

        [...(roleResults?.data || []), ...(musicalResults?.data || [])]?.forEach(role => {
          if (!seenIds?.has(role?.id)) {
            seenIds?.add(role?.id);
            allResults?.push(role);
          }
        });

        return { data: allResults, error: null };
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching roles:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in getRoles:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch roles' }
      };
    }
  },

  // Get roles with song count for each role
  async getRolesWithSongCounts(searchTerm = '') {
    try {
      const { data: roles, error: rolesError } = await this.getRoles(searchTerm);
      
      if (rolesError) return { data: null, error: rolesError };

      // Get song counts for each role
      const rolesWithCounts = await Promise.all(
        (roles || [])?.map(async (role) => {
          const songCount = await this.getSongCountForRole(role?.id);
          return {
            ...role,
            song_count: songCount?.data || 0
          };
        })
      );

      return { data: rolesWithCounts, error: null };
    } catch (err) {
      console.error('Service error in getRolesWithSongCounts:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch roles with song counts' }
      };
    }
  },

  // Get song count for a specific role
  async getSongCountForRole(roleId) {
    try {
      const { count, error } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', roleId);

      if (error) {
        console.error('Error fetching song count for role:', error);
        return { data: 0, error };
      }

      return { data: count || 0, error: null };
    } catch (err) {
      console.error('Service error in getSongCountForRole:', err);
      return { 
        data: 0, 
        error: { message: 'Failed to fetch song count' }
      };
    }
  },

  // Get single role by ID with detailed information
  async getRoleById(roleId) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          vocal_range,
          voice_type,
          role_type,
          is_principal,
          age_range,
          created_at,
          updated_at,
          musical_id,
          assigned_talent_id,
          musicals (
            id,
            title,
            poster_url,
            composer,
            lyricist,
            book_writer,
            year_premiered,
            genre,
            description,
            is_active
          ),
          talent_profiles (
            id,
            display_name,
            first_name,
            last_name,
            email,
            phone,
            headshot_url,
            website,
            instagram,
            tiktok,
            youtube
          )
        `)?.eq('id', roleId)?.single();

      if (error) {
        console.error('Error fetching role by ID:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in getRoleById:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch role details' }
      };
    }
  },

  // Get songs associated with a role
  async getSongsForRole(roleId) {
    try {
      const { data, error } = await supabase?.from('song_roles')?.select(`
          id,
          is_solo,
          vocal_part,
          created_at,
          songs!inner (
            id,
            title,
            act_number,
            song_order,
            key_signature,
            is_featured,
            musical_id,
            created_at,
            updated_at,
            musicals!inner (
              id,
              title,
              poster_url
            )
          )
        `)?.eq('role_id', roleId)?.order('songs(act_number)')?.order('songs(song_order)');

      if (error) {
        console.error('Error fetching songs for role:', error);
        return { data: null, error };
      }

      // Deduplicate songs by title per musical (database-level safeguard)
      const deduplicatedData = this.deduplicateSongsByTitle(data);

      return { data: deduplicatedData, error: null };
    } catch (err) {
      console.error('Service error in getSongsForRole:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch songs for role' }
      };
    }
  },

  // Helper function to deduplicate songs by title per musical
  deduplicateSongsByTitle(songRoles) {
    if (!songRoles || songRoles?.length === 0) return songRoles;
    
    const seenTitles = new Map();
    
    return songRoles?.filter(songRole => {
      const song = songRole?.songs;
      if (!song?.title || !song?.musical_id) return true;
      
      // Create a unique key: musical_id + normalized title
      const normalizedTitle = song?.title?.toLowerCase()?.trim();
      const uniqueKey = `${song?.musical_id}_${normalizedTitle}`;
      
      // If we've seen this song before, skip it (keep first occurrence)
      if (seenTitles?.has(uniqueKey)) {
        return false;
      }
      
      // Mark this song as seen and include it
      seenTitles?.set(uniqueKey, true);
      return true;
    });
  },

  // Enhanced search roles with completely different approach to avoid parsing errors
  async searchRolesFuzzy(searchTerm) {
    try {
      if (!searchTerm?.trim()) {
        return this.getRolesWithSongCounts();
      }

      const searchLower = searchTerm?.trim()?.toLowerCase();

      // Use a completely different approach - multiple separate queries instead of OR
      const queries = [
        // Character name search - MOST IMPORTANT for finding newly created characters
        supabase?.from('roles')?.select(`
            id,
            character_name,
            description,
            vocal_range,
            voice_type,
            role_type,
            is_principal,
            age_range,
            created_at,
            updated_at,
            musical_id,
            assigned_talent_id,
            musicals (
              id,
              title,
              poster_url,
              composer,
              lyricist,
              year_premiered,
              genre,
              is_active
            )
          `)?.ilike('character_name', `%${searchLower}%`),
        
        // Description search  
        supabase?.from('roles')?.select(`
            id,
            character_name,
            description,
            vocal_range,
            voice_type,
            role_type,
            is_principal,
            age_range,
            created_at,
            updated_at,
            musical_id,
            assigned_talent_id,
            musicals (
              id,
              title,
              poster_url,
              composer,
              lyricist,
              year_premiered,
              genre,
              is_active
            )
          `)?.ilike('description', `%${searchLower}%`),

        // Musical title search (only for roles with musicals)
        supabase?.from('roles')?.select(`
            id,
            character_name,
            description,
            vocal_range,
            voice_type,
            role_type,
            is_principal,
            age_range,
            created_at,
            updated_at,
            musical_id,
            assigned_talent_id,
            musicals!inner (
              id,
              title,
              poster_url,
              composer,
              lyricist,
              year_premiered,
              genre,
              is_active
            )
          `)?.filter('musicals.title', 'ilike', `%${searchLower}%`)
      ];

      // Execute all queries in parallel
      const results = await Promise.all(queries);
      
      // Check for errors
      const errors = results?.filter(result => result?.error);
      if (errors?.length > 0) {
        console.error('Error in search queries:', errors);
        // Try fallback to basic search
        return this.getRolesWithSongCounts(searchTerm);
      }

      // Combine and deduplicate results
      const allRoles = [];
      const seenIds = new Set();

      results?.forEach(result => {
        (result?.data || [])?.forEach(role => {
          if (!seenIds?.has(role?.id)) {
            seenIds?.add(role?.id);
            allRoles?.push(role);
          }
        });
      });

      // Add song counts
      const rolesWithCounts = await Promise.all(
        (allRoles || [])?.map(async (role) => {
          const songCount = await this.getSongCountForRole(role?.id);
          return {
            ...role,
            song_count: songCount?.data || 0
          };
        })
      );

      // Enhanced relevance sorting - prioritize character name matches
      const sorted = rolesWithCounts?.sort((a, b) => {
        const aCharacterName = (a?.character_name || '')?.toLowerCase();
        const bCharacterName = (b?.character_name || '')?.toLowerCase();
        const aMusicalTitle = (a?.musicals?.title || '')?.toLowerCase();
        const bMusicalTitle = (b?.musicals?.title || '')?.toLowerCase();

        // Priority 1: Exact character name matches (CRITICAL for new characters)
        if (aCharacterName === searchLower && bCharacterName !== searchLower) return -1;
        if (bCharacterName === searchLower && aCharacterName !== searchLower) return 1;

        // Priority 2: Character name starts with search term
        if (aCharacterName?.startsWith(searchLower) && !bCharacterName?.startsWith(searchLower)) return -1;
        if (bCharacterName?.startsWith(searchLower) && !aCharacterName?.startsWith(searchLower)) return 1;

        // Priority 3: Character name contains search term
        const aCharacterMatch = aCharacterName?.includes(searchLower);
        const bCharacterMatch = bCharacterName?.includes(searchLower);
        if (aCharacterMatch && !bCharacterMatch) return -1;
        if (bCharacterMatch && !aCharacterMatch) return 1;

        // Priority 4: Musical title matches (only if both have musicals)
        if (a?.musicals?.title && b?.musicals?.title) {
          const aMusicalMatch = aMusicalTitle?.includes(searchLower);
          const bMusicalMatch = bMusicalTitle?.includes(searchLower);
          if (aMusicalMatch && !bMusicalMatch) return -1;
          if (bMusicalMatch && !aMusicalMatch) return 1;
        }

        // Priority 5: Roles with musicals come after roles without musicals for character searches
        if (!a?.musical_id && b?.musical_id) return 1;
        if (a?.musical_id && !b?.musical_id) return -1;

        // Finally by character name alphabetically
        return aCharacterName?.localeCompare(bCharacterName);
      });

      return { data: sorted, error: null };
    } catch (err) {
      console.error('Service error in searchRolesFuzzy:', err);
      return { 
        data: null, 
        error: { message: 'Search failed' }
      };
    }
  },

  // Create new role
  async createRole(roleData, userId) {
    try {
      // Get current auth session to ensure we have the right user ID
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session?.user) {
        console.error('Authentication required for role creation:', sessionError);
        return { 
          data: null, 
          error: { message: 'Authentication required. Please log in again.' }
        };
      }

      // Use auth.uid() directly instead of relying on passed userId
      const authUserId = session?.user?.id;
      
      const { data, error } = await supabase?.from('roles')?.insert({
          ...roleData,
          created_by: authUserId  // Use authenticated user ID from session
        })?.select()?.single();

      if (error) {
        console.error('Error creating role:', error);
        
        // Provide more specific error messages for RLS policy violations
        if (error?.code === '42501') {
          return { 
            data: null, 
            error: { 
              message: 'Permission denied. Please refresh the page and try again.', 
              code: error?.code 
            }
          };
        }
        
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in createRole:', err);
      return { 
        data: null, 
        error: { message: 'Failed to create role. Please try again.' }
      };
    }
  },

  // Update role
  async updateRole(roleId, roleData, userId) {
    try {
      const { data, error } = await supabase?.from('roles')?.update({
          ...roleData,
          updated_by: userId
        })?.eq('id', roleId)?.select(`
          id,
          character_name,
          description,
          vocal_range,
          voice_type,
          role_type,
          is_principal,
          age_range,
          created_at,
          updated_at,
          musical_id,
          assigned_talent_id,
          musicals (
            id,
            title,
            poster_url,
            composer,
            lyricist,
            book_writer,
            year_premiered,
            genre,
            description,
            is_active
          )
        `)?.single();

      if (error) {
        console.error('Error updating role:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in updateRole:', err);
      return { 
        data: null, 
        error: { message: 'Failed to update role' }
      };
    }
  },

  // Delete role
  async deleteRole(roleId) {
    try {
      const { error } = await supabase?.from('roles')?.delete()?.eq('id', roleId);

      if (error) {
        console.error('Error deleting role:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Service error in deleteRole:', err);
      return { 
        error: { message: 'Failed to delete role' }
      };
    }
  },

  // Get roles by musical ID
  async getRolesByMusical(musicalId) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          vocal_range,
          voice_type,
          role_type,
          is_principal,
          age_range,
          assigned_talent_id
        `)?.eq('musical_id', musicalId)?.order('role_type', { ascending: true })?.order('character_name', { ascending: true });

      if (error) {
        console.error('Error fetching roles by musical:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in getRolesByMusical:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch roles for musical' }
      };
    }
  },

  // Get role statistics
  async getRoleStatistics() {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          role_type,
          voice_type,
          is_principal,
          musical:musicals!inner(is_active)
        `);

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        by_type: {},
        by_voice: {},
        principal: data?.filter(r => r?.is_principal)?.length || 0
      };

      data?.forEach(role => {
        if (role?.role_type) {
          stats.by_type[role.role_type] = (stats?.by_type?.[role?.role_type] || 0) + 1;
        }
        if (role?.voice_type) {
          stats.by_voice[role.voice_type] = (stats?.by_voice?.[role?.voice_type] || 0) + 1;
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Create song-role relationship
  async createSongRoleRelationship(relationshipData) {
    try {
      const { data, error } = await supabase?.from('song_roles')?.insert({
          role_id: relationshipData?.role_id,
          song_id: relationshipData?.song_id,
          is_solo: relationshipData?.is_solo || false,
          vocal_part: relationshipData?.vocal_part || null
        })?.select()?.single();

      if (error) {
        console.error('Error creating song-role relationship:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in createSongRoleRelationship:', err);
      return { 
        data: null, 
        error: { message: 'Failed to assign song to role' }
      };
    }
  },

  // Remove song-role relationship
  async removeSongRoleRelationship(songRoleId) {
    try {
      const { error } = await supabase?.from('song_roles')?.delete()?.eq('id', songRoleId);

      if (error) {
        console.error('Error removing song-role relationship:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Service error in removeSongRoleRelationship:', err);
      return { 
        error: { message: 'Failed to remove song from role' }
      };
    }
  },

  // Update song-role relationship
  async updateSongRoleRelationship(songRoleId, updates) {
    try {
      const { data, error } = await supabase?.from('song_roles')?.update({
          is_solo: updates?.is_solo,
          vocal_part: updates?.vocal_part
        })?.eq('id', songRoleId)?.select()?.single();

      if (error) {
        console.error('Error updating song-role relationship:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in updateSongRoleRelationship:', err);
      return { 
        data: null, 
        error: { message: 'Failed to update song-role relationship' }
      };
    }
  },

  // Get all songs from the musical associated with a specific role
  async getSongsFromRoleMusical(roleId) {
    try {
      // First get the role with its musical information
      const { data: roleData, error: roleError } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          musical_id,
          assigned_talent_id,
          musicals (
            id,
            title
          )
        `)?.eq('id', roleId)?.single();

      if (roleError) {
        console.error('Error fetching role musical info:', roleError);
        return { data: null, error: roleError };
      }

      if (!roleData?.musical_id) {
        // Role is not associated with any musical
        return { data: [], error: null };
      }

      // Get all songs from the role's musical
      const { data: songsData, error: songsError } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          song_order,
          key_signature,
          is_featured,
          created_at,
          updated_at,
          musical_id
        `)?.eq('musical_id', roleData?.musical_id)?.order('act_number', { ascending: true })?.order('song_order', { ascending: true });

      if (songsError) {
        console.error('Error fetching songs from role musical:', songsError);
        return { data: null, error: songsError };
      }

      // Get currently assigned songs for this role to mark them as selected
      const { data: assignedSongs, error: assignedError } = await supabase?.from('song_roles')?.select('song_id')?.eq('role_id', roleId);

      if (assignedError) {
        console.error('Error fetching assigned songs:', assignedError);
        return { data: null, error: assignedError };
      }

      const assignedSongIds = new Set((assignedSongs || [])?.map(sr => sr?.song_id));

      // Mark songs as assigned or unassigned
      const songsWithAssignmentStatus = (songsData || [])?.map(song => ({
        ...song,
        is_assigned: assignedSongIds?.has(song?.id),
        musical_title: roleData?.musicals?.title
      }));

      return { 
        data: songsWithAssignmentStatus, 
        error: null,
        musical: roleData?.musicals
      };
    } catch (err) {
      console.error('Service error in getSongsFromRoleMusical:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch songs from role musical' }
      };
    }
  },

  // Get roles assigned to a specific talent
  async getRolesByTalent(talentId) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          vocal_range,
          voice_type,
          role_type,
          is_principal,
          age_range,
          created_at,
          updated_at,
          musical_id,
          musicals (
            id,
            title,
            poster_url,
            composer,
            lyricist,
            year_premiered,
            genre
          )
        `)?.eq('assigned_talent_id', talentId)?.order('character_name', { ascending: true });

      if (error) {
        console.error('Error fetching roles by talent:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in getRolesByTalent:', err);
      return { 
        data: null, 
        error: { message: 'Failed to fetch roles for talent' }
      };
    }
  },

  // Real-time subscription methods
  /**
   * Subscribe to real-time changes for roles table
   * @param {Object} callbacks - Object with onInsert, onUpdate, onDelete callback functions
   * @param {Object} filter - Optional filter for subscription
   * @returns {Function} Unsubscribe function
   */
  subscribeToRoles(callbacks = {}, filter = null) {
    const { onInsert, onUpdate, onDelete } = callbacks;
    
    const channelConfig = {
      event: '*',
      schema: 'public',
      table: 'roles'
    };

    if (filter) {
      channelConfig.filter = `${filter?.column}=eq.${filter?.value}`;
    }

    const channel = supabase?.channel('roles-changes')?.on('postgres_changes', channelConfig, (payload) => {
        const { eventType, new: newRecord, old: oldRecord } = payload;
        
        switch (eventType) {
          case 'INSERT':
            onInsert?.(newRecord);
            break;
          case 'UPDATE':
            onUpdate?.(newRecord, oldRecord);
            break;
          case 'DELETE':
            onDelete?.(oldRecord);
            break;
        }
      })?.subscribe();

    // Return unsubscribe function
    return () => {
      supabase?.removeChannel(channel);
    };
  },

  /**
   * Subscribe to real-time changes for a specific musical's roles
   * @param {string} musicalId - Musical ID to filter by
   * @param {Object} callbacks - Object with onInsert, onUpdate, onDelete callback functions
   * @returns {Function} Unsubscribe function
   */
  subscribeToMusicalRoles(musicalId, callbacks = {}) {
    return RoleService?.subscribeToRoles(callbacks, {
      column: 'musical_id',
      value: musicalId
    });
  }
};

export default RoleService;

export const updateRole = async (roleId, roleData) => {
  try {
    const { data, error } = await supabase?.from('roles')?.update(roleData)?.eq('id', roleId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
};

export const assignTalentToRole = async (roleId, talentId) => {
  try {
    const { data, error } = await supabase?.from('roles')?.update({ assigned_talent_id: talentId })?.eq('id', roleId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error assigning talent to role:', error);
    throw error;
  }
};

// Export the RoleService object as default and named export
export { RoleService };

function roleService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: roleService is not implemented yet.', args);
  return null;
}

export { roleService };